import { useState } from "react";
import { useSelector } from "react-redux";
import SearchBar from "../../components/searchBar";
import CommonTable from "../../components/commonTable";
import ColumnVisibility from "../../components/columnVisibility";
import CustomPagination from "../../components/pagination";
import { getCouponsSummaryColumns, CouponsSummaryRecord } from "./columns";
import { useGetComplimentaryCouponsSummaryQuery } from "../../services/complimentaryCoupon";
import usePermissions from "../../hooks/usePermissions";
import "./styles.scss";

/* ─── Component ──────────────────────────────────────────────────────── */

const CouponsSummary = () => {
  const { hasPermission } = usePermissions();
  const canView = hasPermission('COUPONS_SUMMARY_VIEW_ALL') || hasPermission('COUPONS_SUMMARY_VIEW_OWN');

  const [searchText, setSearchText] = useState("");
  const [page,       setPage]       = useState(1);
  const [limit,      setLimit]      = useState(10);

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
    branch: true, name: true, phoneNumber: true, profile: true, planName: true,
    coupons: true, packageExpiry: true,
  });

  // branch comes from the global header (Redux), not a per-page filter
  const branchId         = useSelector((state: any) => state.branch.selectedBranch);
  const selectedBranchId = typeof branchId === 'object' ? (branchId?._id || branchId?.id) : branchId;

  const { data, isLoading } = useGetComplimentaryCouponsSummaryQuery({
    search:   searchText || undefined,
    branchId: selectedBranchId || undefined,
    page,
    limit,
  }, { skip: !canView });

  const apiData = data as any;
  const records: CouponsSummaryRecord[] = apiData?.data ?? [];
  const total: number = apiData?.total ?? apiData?.pagination?.total ?? records.length;

  const allColumns = getCouponsSummaryColumns();
  const columns    = allColumns.filter(col => visibleColumns[col.key]);

  const handleColumnToggle = (columnKey: string) => {
    setVisibleColumns(prev => ({ ...prev, [columnKey]: !prev[columnKey] }));
  };

  if (!canView) {
    return (
      <div className="coupons-summary-page">
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--muted)' }}>
          You don't have permission to view coupons summary.
        </div>
      </div>
    );
  }

  return (
    <div className="coupons-summary-page">
      <div className="header-section">
        <div className="left-col">
          <SearchBar
            value={searchText}
            onChange={(val: string) => { setSearchText(val); setPage(1); }}
            placeholder="Search by name or phone..."
          />
        </div>
        <div className="right-col">
          <ColumnVisibility
            columns={allColumns}
            visibleColumns={visibleColumns}
            onColumnToggle={handleColumnToggle}
          />
        </div>
      </div>

      <div className="coupons-summary-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={records}
          loading={isLoading}
          pagination={false}
          rowKey={(record: CouponsSummaryRecord, index) => record.membershipId ?? `${index}`}
          scroll={{ x: 1200 }}
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={total}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />
    </div>
  );
};

export default CouponsSummary;
